import {
  Button,
  Distribute,
  Dropdown,
  Icon,
  Link,
  SvgCheckCircleFill,
  SvgChevronDown,
  SvgDomain,
  SvgLock,
  SvgPublicOn,
  SvgUsers,
  Text,
} from '@loomhq/lens';
import classNames from 'classnames';
import pluralize from 'pluralize';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { openModal } from 'actions/modals';
import {
  PRIVACY_CHANGE_CONFIRMATION_MODAL,
  TEAM_INVITE_MODAL,
} from 'constants/modalTypes';
import { updateVideoSettings } from 'creators/shareVideo';

import { useGetSelectedWorkspace } from 'hooks/workspace';
import { hasScope } from 'selectors/scopes';

import SearchEngineIndexing from './SearchEngineIndexing';
import {
  VIDEO_INVITE_PEOPLE_ACTION,
  WORKSPACE_PRIVACY_ACCESS,
} from '../../../../../../shared/constants/scopes';
import {
  VIDEO_PRIVACY_CUSTOM_ACCESS,
  VIDEO_PRIVACY_OWNER,
  VIDEO_PRIVACY_PUBLIC,
  VIDEO_PRIVACY_WORKSPACE,
} from '../../../../../../shared/constants/video';

import styles from './styles.module.less';

const getOptionTitle = (privacy, workspaceName) => {
  switch (privacy) {
    case VIDEO_PRIVACY_OWNER: {
      return 'Private';
    }
    case VIDEO_PRIVACY_WORKSPACE:
      return `Everyone at ${workspaceName}`;
    case VIDEO_PRIVACY_CUSTOM_ACCESS:
      return 'Specific people';
    case VIDEO_PRIVACY_PUBLIC:
    default:
      return 'Public';
  }
};

const getOptionIcon = privacy => {
  switch (privacy) {
    case VIDEO_PRIVACY_OWNER:
      return <SvgLock />;
    case VIDEO_PRIVACY_WORKSPACE:
      return <SvgDomain />;
    case VIDEO_PRIVACY_CUSTOM_ACCESS:
      return <SvgUsers />;
    case VIDEO_PRIVACY_PUBLIC:
    default:
      return <SvgPublicOn />;
  }
};

const PrivateContent = ({ onCustomViewerClick, isFirstVideoShareModal }) => {
  const { video, shareVideoAliases } = useSelector(state => state.share_video);
  const selectedWorkspace = useGetSelectedWorkspace();
  const dispatch = useDispatch();

  const scopes = useSelector(state => ({
    hasPrivateContentAccess: hasScope(state, WORKSPACE_PRIVACY_ACCESS),
    hasCustomAccessAccess: hasScope(state, VIDEO_INVITE_PEOPLE_ACTION),
  }));

  const allowWorkspacePrivacyOption =
    scopes.hasPrivateContentAccess || video.privacy === VIDEO_PRIVACY_WORKSPACE;

  const allowCustomAccessOption =
    scopes.hasCustomAccessAccess || video.permitted_viewers_only;

  const publicOption = {
    key: VIDEO_PRIVACY_PUBLIC,
    title: (
      <div
        className={classNames('mx:xsmall my:small flex', styles.privacyItem)}
      >
        <Icon icon={getOptionIcon(VIDEO_PRIVACY_PUBLIC)} size={4} />
        <span className={classNames('ml:small', styles.privacyItemBody)}>
          <Text fontWeight="medium">
            {getOptionTitle(VIDEO_PRIVACY_PUBLIC)}
          </Text>
          <Text color="bodyDimmed" size="small">
            Anyone with the link can view. No log in required.
          </Text>
        </span>
        {(!video.privacy || video.privacy === VIDEO_PRIVACY_PUBLIC) &&
          !video.permitted_viewers_only && (
            <Icon icon={<SvgCheckCircleFill />} size={4} />
          )}
      </div>
    ),
    onClick: () =>
      dispatch(updateVideoSettings({ privacy: VIDEO_PRIVACY_PUBLIC })),
  };

  const workspaceOption = {
    key: VIDEO_PRIVACY_WORKSPACE,
    title: (
      <div
        className={classNames('mx:xsmall my:small flex', styles.privacyItem)}
      >
        <Icon icon={getOptionIcon(VIDEO_PRIVACY_WORKSPACE)} size={4} />
        <span className={classNames('ml:small', styles.privacyItemBody)}>
          <Text fontWeight="medium">
            {getOptionTitle(VIDEO_PRIVACY_WORKSPACE, selectedWorkspace.name)}
          </Text>
          <Text color="bodyDimmed" size="small">
            {selectedWorkspace.members} Workspace{' '}
            {pluralize('member', selectedWorkspace.members)} can view. Login
            required.
          </Text>
        </span>
        {video.privacy === VIDEO_PRIVACY_WORKSPACE &&
          !video.permitted_viewers_only && (
            <Icon icon={<SvgCheckCircleFill />} size={4} />
          )}
      </div>
    ),
    onClick: () => {
      const updates = { privacy: VIDEO_PRIVACY_WORKSPACE };

      if (shareVideoAliases?.length) {
        dispatch(
          openModal(PRIVACY_CHANGE_CONFIRMATION_MODAL, {
            onConfirm: () => dispatch(updateVideoSettings(updates)),
          })
        );
      } else {
        dispatch(updateVideoSettings(updates));
      }
    },
  };

  const ownerOption = {
    key: VIDEO_PRIVACY_OWNER,
    title: (
      <div
        className={classNames('mx:xsmall my:small flex', styles.privacyItem)}
      >
        <Icon icon={getOptionIcon(VIDEO_PRIVACY_OWNER)} size={3} />
        <span className={classNames('ml:small', styles.privacyItemBody)}>
          <Text fontWeight="medium">{getOptionTitle(VIDEO_PRIVACY_OWNER)}</Text>
          <Text color="bodyDimmed" size="small">
            The recording is visible to you only.
          </Text>
        </span>
        {video.privacy === VIDEO_PRIVACY_OWNER &&
          !video.permitted_viewers_only && (
            <Icon icon={<SvgCheckCircleFill />} size={4} />
          )}
      </div>
    ),
    onClick: () => {
      const updates = { privacy: VIDEO_PRIVACY_OWNER };

      if (shareVideoAliases?.length) {
        dispatch(
          openModal(PRIVACY_CHANGE_CONFIRMATION_MODAL, {
            onConfirm: () => dispatch(updateVideoSettings(updates)),
          })
        );
      } else {
        dispatch(updateVideoSettings(updates));
      }
    },
  };

  const customAccessOption = {
    key: VIDEO_PRIVACY_CUSTOM_ACCESS,
    title: (
      <div
        className={classNames('mx:xsmall my:small flex', styles.privacyItem)}
      >
        <Icon icon={getOptionIcon(VIDEO_PRIVACY_CUSTOM_ACCESS)} size={4} />
        <span className={classNames('ml:small', styles.privacyItemBody)}>
          <Text fontWeight="medium">
            {getOptionTitle(VIDEO_PRIVACY_CUSTOM_ACCESS)}
          </Text>
          <Text color="bodyDimmed" size="small">
            Only you and those you personally invite can view.
          </Text>
        </span>
        {video.permitted_viewers_only && (
          <Icon icon={<SvgCheckCircleFill />} size={4} />
        )}
      </div>
    ),
    onClick: () => {
      onCustomViewerClick();
    },
  };

  const options = [publicOption];

  if (allowWorkspacePrivacyOption) {
    options.push(workspaceOption);
  }

  if (allowCustomAccessOption) {
    options.push(customAccessOption);
  }

  options.push(ownerOption);

  return (
    <>
      <Text color="bodyDimmed" size="small" className="mt:small">
        Manage your share link’s privacy
      </Text>
      <div className="my:medium">
        <Dropdown
          menuZIndex={isFirstVideoShareModal ? 1500 : 100} // Need to have z-index greater than the backdrop component (1000) if displayed in the first video share modal
          id={styles.privacyDropdown}
          menuMaxWidth="278px"
          trigger={
            <Button
              hasFullWidth
              icon={<SvgChevronDown />}
              iconPosition="right"
              className={styles.triggerBtn}
            >
              <Distribute gap="small" alignment="start">
                <Icon
                  icon={getOptionIcon(
                    video.permitted_viewers_only
                      ? VIDEO_PRIVACY_CUSTOM_ACCESS
                      : video.privacy
                  )}
                />
                <div className={styles.triggerBtnBody}>
                  <Text hasEllipsis>
                    {getOptionTitle(
                      video.permitted_viewers_only
                        ? VIDEO_PRIVACY_CUSTOM_ACCESS
                        : video.privacy,
                      selectedWorkspace.name
                    )}
                  </Text>
                </div>
              </Distribute>
            </Button>
          }
          options={options}
        />
      </div>

      <>
        {(!video.privacy || video.privacy === VIDEO_PRIVACY_PUBLIC) && (
          <SearchEngineIndexing video={video} />
        )}
        {video.permitted_viewers_only ? (
          <Text size="small" className={styles.nowrap}>
            Shared with {video.viewer_permission_list?.length || 0}
            {` `}
            <b>{pluralize('other', video.viewer_permission_list?.length)}</b>.
            {` `}
            <Link
              onClick={e => {
                e.preventDefault();
                onCustomViewerClick();
              }}
            >
              Manage
            </Link>
          </Text>
        ) : (
          video.privacy === VIDEO_PRIVACY_WORKSPACE && (
            <Text size="small" className={styles.nowrap}>
              Want more teammates to view?{' '}
              <Link
                onClick={e => {
                  e.preventDefault();
                  dispatch(openModal(TEAM_INVITE_MODAL));
                }}
              >
                Invite others
              </Link>
            </Text>
          )
        )}
      </>
    </>
  );
};

export default PrivateContent;
